import type { GameStateManager } from '../game/GameState';
import type { GameData, Researcher, LocationId, ResearchTypeId, ResearchType, GameSpeed } from '../game/types';
import { ResearchSystem } from '../systems/ResearchSystem';
import { ResourceSystem } from '../systems/ResourceSystem';

export class ResearchModal {
  private currentResearcher: Researcher | null = null;
  private currentCell: LocationId | null = null;
  private selectedTypes: Set<ResearchTypeId> = new Set();
  private keyDownHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(
    private container: HTMLElement,
    private gameState: GameStateManager,
    private gameData: GameData,
    private onConfirm: (researcherId: string, types: ResearchTypeId[], cellId: LocationId) => void,
    private onCancel: () => void,
    private onSpeedRestore?: (speed: GameSpeed) => void
  ) {
    this.setup();
  }

  private setup(): void {
    this.container.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).classList.contains('modal-backdrop')) {
        this.hide();
      }
    });
  }

  private handleKeyDown(event: KeyboardEvent): void {
    // Only handle if modal is open
    if (this.container.classList.contains('hidden')) return;

    // Number keys 1-9 for selection
    if (event.code.startsWith('Digit') && event.code.length === 6) {
      const digit = parseInt(event.code[5]);
      if (digit >= 1 && digit <= 9) {
        event.preventDefault();
        event.stopPropagation();
        this.selectByIndex(digit - 1);
      }
    } else if (event.code === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.confirm();
    } else if (event.code === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.hide();
    }
  }

  private selectByIndex(index: number): void {
    const researchTypes = this.gameData.researchTypes;
    if (index >= 0 && index < researchTypes.length) {
      this.toggleResearchType(researchTypes[index].id);
    }
  }

  private confirm(): void {
    if (this.currentCell && this.currentResearcher) {
      const anomaly = this.gameState.getAnomalyInCell(this.currentCell);
      if (!anomaly) return;

      const antiepistemicCount = anomaly.knownProperties.filter(p => p === 'Antiepistemic').length;
      const minimumRequired = 3 + antiepistemicCount;
      const maximumAllowed = 6;
      const isValid = this.selectedTypes.size >= minimumRequired && this.selectedTypes.size <= maximumAllowed;
      
      const state = this.gameState.getState();
      const totalCosts = this.calculateTotalCosts();
      
      // Check if Neutralize is selected and researcher has silver bullet
      const needsSilverBullet = this.selectedTypes.has('Neutralize');
      const hasSilverBullet = this.currentResearcher.silverBullet !== null;
      
      const canAfford = state.budget >= totalCosts.budget &&
                       state.chemicals >= totalCosts.chemicals &&
                       state.computingAvailable >= totalCosts.computation &&
                       (!needsSilverBullet || hasSilverBullet);

      if (isValid && canAfford) {
        this.onConfirm(this.currentResearcher.id, Array.from(this.selectedTypes), this.currentCell);
        this.hide();
      }
    }
  }

  show(researcher: Researcher, cellId: LocationId): void {
    this.currentResearcher = researcher;
    this.currentCell = cellId;
    this.selectedTypes.clear();
    this.render();
    this.container.classList.remove('hidden');
    this.gameState.setPausedByModal(true);
    
    // Attach keyboard handler to document when modal opens
    this.keyDownHandler = (e: KeyboardEvent) => this.handleKeyDown(e);
    document.addEventListener('keydown', this.keyDownHandler);
    
    // Focus the modal content to ensure it can receive focus
    const modalContent = this.container.querySelector('.modal-content') as HTMLElement;
    if (modalContent) {
      modalContent.focus();
    }
  }

  hide(): void {
    this.container.classList.add('hidden');
    this.gameState.setPausedByModal(false);
    const restoredSpeed = this.gameState.restorePauseStateAfterModal();
    if (restoredSpeed !== null && this.onSpeedRestore) {
      this.onSpeedRestore(restoredSpeed);
    }
    
    // Remove keyboard handler when modal closes
    if (this.keyDownHandler) {
      document.removeEventListener('keydown', this.keyDownHandler);
      this.keyDownHandler = null;
    }
    
    this.onCancel();
  }

  isOpen(): boolean {
    return !this.container.classList.contains('hidden');
  }

  private render(): void {
    if (!this.currentResearcher || !this.currentCell) return;

    const strings = this.gameData.uiStrings;
    const researchSystem = new ResearchSystem(this.gameState, this.gameData, new ResourceSystem(this.gameState, this.gameData));
    const anomaly = this.gameState.getAnomalyInCell(this.currentCell);
    
    if (!anomaly) {
      this.hide();
      return;
    }

    const researchTypes = this.gameData.researchTypes;
    const totalTime = this.calculateTotalTime();
    const totalCosts = this.calculateTotalCosts();
    const state = this.gameState.getState();

    // Check if Neutralize is selected and researcher has silver bullet
    const needsSilverBullet = this.selectedTypes.has('Neutralize');
    const hasSilverBullet = this.currentResearcher.silverBullet !== null;
    
    const canAfford = state.budget >= totalCosts.budget &&
                     state.chemicals >= totalCosts.chemicals &&
                     state.computingAvailable >= totalCosts.computation &&
                     (!needsSilverBullet || hasSilverBullet);

    // Calculate minimum based on Antiepistemic
    const antiepistemicCount = anomaly.knownProperties.filter(p => p === 'Antiepistemic').length;
    const minimumRequired = 3 + antiepistemicCount;
    const maximumAllowed = 6;
    const isValid = this.selectedTypes.size >= minimumRequired && this.selectedTypes.size <= maximumAllowed;
    
    // Store minimum for validation message
    const currentMinimum = minimumRequired;

    this.container.innerHTML = `
      <div class="modal-backdrop"></div>
      <div class="modal-content" tabindex="0">
        <div class="modal-header">
          <h2>${strings.modals?.research_title || 'Research Anomaly'}</h2>
          <button class="modal-close" onclick="this.closest('.modal').classList.add('hidden')">×</button>
        </div>
        <div class="modal-body">
          <div class="research-anomaly-info">
            <div>${anomaly.data.name}</div>
            <div>Known Properties: ${anomaly.knownProperties.length}/3</div>
          </div>
          <div class="research-types-selection">
            <div class="selection-header">${strings.modals?.select_types || 'Select Research Types'}</div>
            <div class="selection-constraint">Minimum: ${minimumRequired} types (${antiepistemicCount > 0 ? `+${antiepistemicCount} from Antiepistemic` : 'base 3'}) - Maximum: ${maximumAllowed} types</div>
            <div class="research-types-list">
              ${researchTypes.map(rt => this.renderResearchType(rt)).join('')}
            </div>
          </div>
          <div class="research-summary">
            <div class="summary-item">
              <span>${strings.labels?.total_time || 'Total Time'}:</span>
              <span>${totalTime} ticks</span>
            </div>
            <div class="summary-item">
              <span>${strings.labels?.total_cost || 'Total Cost'}:</span>
              <span>Budget: ${totalCosts.budget}, Chemicals: ${totalCosts.chemicals}, Computing: ${totalCosts.computation}</span>
            </div>
            <div class="summary-item">
              <span>${strings.labels?.available_resources || 'Available Resources'}:</span>
              <span>Budget: ${state.budget}, Chemicals: ${state.chemicals}, Computing: ${state.computingAvailable}</span>
            </div>
            ${needsSilverBullet && !hasSilverBullet ? `
            <div class="summary-item error-message">
              <span style="color: #ff6b6b;">⚠ ${this.currentResearcher.data.name} does not have a Silver Bullet required for Neutralize</span>
            </div>
            ` : ''}
            ${!isValid && this.selectedTypes.size < minimumRequired ? `
            <div class="summary-item error-message">
              <span style="color: #ff6b6b;">⚠ Minimum ${minimumRequired} research types required (Antiepistemic property)</span>
            </div>
            ` : ''}
          </div>
        </div>
        <div class="modal-footer">
          <button class="button-cancel" data-action="cancel">${strings.buttons?.cancel || 'Cancel'}</button>
          <button class="button-confirm ${!isValid || !canAfford ? 'disabled' : ''}" 
                  ${!isValid || !canAfford ? 'disabled' : ''}
                  data-action="confirm">${strings.buttons?.confirm || 'Confirm'}</button>
        </div>
      </div>
    `;

    // Attach event listeners
    researchTypes.forEach(rt => {
      const checkbox = this.container.querySelector(`[data-research-type="${rt.id}"]`) as HTMLInputElement;
      if (checkbox) {
        checkbox.addEventListener('change', () => this.toggleResearchType(rt.id));
      }
    });

    // Attach button event listeners
    const cancelButton = this.container.querySelector('[data-action="cancel"]') as HTMLElement;
    if (cancelButton) {
      cancelButton.addEventListener('click', () => {
        const closeButton = this.container.querySelector('.modal-close') as HTMLElement;
        if (closeButton) closeButton.click();
      });
    }

    const confirmButton = this.container.querySelector('[data-action="confirm"]') as HTMLElement;
    if (confirmButton) {
      confirmButton.addEventListener('click', () => {
        this.confirm();
      });
    }
  }

  private renderResearchType(rt: ResearchType): string {
    const isSelected = this.selectedTypes.has(rt.id);
    const costs = rt.costs || [];
    
    // Check if Warp is unlocked
    const isWarp = rt.id === 'Warp';
    const isWarpUnlocked = this.gameState.isWarpUnlocked();
    const isDisabled = isWarp && !isWarpUnlocked;
    
    const costText = costs.map((c) => {
      if (c.time) return `Time: ${c.time}`;
      if (c.budget) return `Budget: ${c.budget}`;
      if (c.chemicals) return `Chemicals: ${c.chemicals}`;
      if (c.computation) return `Computing: ${c.computation}`;
      if (c.silver_bullets) return `Silver Bullet: 1`;
      if (c.unlock_warp) return `Requires: Warp Unlocked`;
      return '';
    }).filter(Boolean).join(', ');

    return `
      <label class="research-type-item ${isDisabled ? 'disabled' : ''}" data-tooltip-key="research_type.${rt.id.toLowerCase()}">
        <input type="checkbox" data-research-type="${rt.id}" ${isSelected ? 'checked' : ''} ${isDisabled ? 'disabled' : ''}>
        <span class="research-type-name">${rt.id}</span>
        <span class="research-type-cost">${costText}</span>
        ${isDisabled ? '<span class="research-type-locked">(Locked)</span>' : ''}
      </label>
    `;
  }

  private toggleResearchType(typeId: ResearchTypeId): void {
    // Don't allow selecting Warp if it's not unlocked
    if (typeId === 'Warp' && !this.gameState.isWarpUnlocked()) {
      return;
    }
    
    if (this.selectedTypes.has(typeId)) {
      this.selectedTypes.delete(typeId);
    } else {
      if (this.selectedTypes.size < 6) {
        this.selectedTypes.add(typeId);
      }
    }
    this.render();
  }

  private calculateTotalTime(): number {
    let total = 0;
    for (const typeId of this.selectedTypes) {
      const rt = this.gameData.researchTypes.find(r => r.id === typeId);
      if (rt) {
        const timeCost = rt.costs.find((c) => c.time !== undefined);
        if (timeCost?.time) {
          total += timeCost.time;
        }
      }
    }
    return total;
  }

  private calculateTotalCosts(): { budget: number; chemicals: number; computation: number } {
    const allCosts = Array.from(this.selectedTypes).flatMap(typeId => {
      const rt = this.gameData.researchTypes.find(r => r.id === typeId);
      return rt?.costs || [];
    });

    let budget = 0;
    let chemicals = 0;
    let computation = 0;

    for (const cost of allCosts) {
      if (cost.budget !== undefined) budget += cost.budget;
      if (cost.chemicals !== undefined) chemicals += cost.chemicals;
      if (cost.computation !== undefined) computation += cost.computation;
    }

    return { budget, chemicals, computation };
  }
}

